import React, { useState, useEffect } from 'react';
import { ApiClient } from '../../api/client';
import type { Tenant } from '../../types';
import { useAuth } from '../../context/AuthContext';
import { useLayout } from '../../context/LayoutContext';
import { downloadTenantsXLSX, downloadTenantsPDF } from '../../utils/export';
import { 
  Building2, 
  Plus, 
  Search, 
  RefreshCw, 
  Edit2, 
  Globe, 
  Mail, 
  Phone, 
  ExternalLink,
  LayoutGrid,
  Table as TableIcon,
  ListCollapse,
  FileSpreadsheet,
  FileText,
  UserCheck,
  ChevronDown,
  ChevronRight,
  ArrowUpDown,
  MapPin,
  Sparkles,
  Package,
  FolderDown,
  Trash2,
  Rocket,
  Check,
  GitCompare,
  HardDrive,
  Database,
  KeyRound,
  Layers,
  FilePenLine
} from 'lucide-react';
import { TemplateBatchModal } from './TemplateBatchModal';
import { QuotaAdminModal } from './QuotaAdminModal';
import type { QuotaDefaults } from '../../api/client';
import { TemplateEditorModal } from './TemplateEditorModal';
import { PhoneInput } from '../common/PhoneInput';
import { TemplateProvisionModal } from '../datahub/TemplateProvisionModal';
import { TenantDiffModal } from './TenantDiffModal';
import { TenantLoginLinkModal } from './TenantLoginLinkModal';
import { usePersistedViewMode } from '../../hooks/usePersistedViewMode';

type ViewMode = 'cards' | 'table' | 'accordion';

const renderQuotaUsage = (t: Tenant, compact = false) => {
  // a ténylegesen érvényes korlát (saját vagy globális); 0 = korlátlan
  const maxStorageMb = t.eff_max_storage_mb ?? t.max_storage_mb ?? 500;
  const usedStorageMb = ((t.storage_used_bytes || 0) / (1024 * 1024));
  const storagePercent = maxStorageMb > 0 ? Math.min(100, Math.round((usedStorageMb / maxStorageMb) * 100)) : 0;

  const maxRecords = t.eff_max_records ?? t.max_records ?? 2000;
  const usedRecords = t.records_count || 0;
  const recordsPercent = maxRecords > 0 ? Math.min(100, Math.round((usedRecords / maxRecords) * 100)) : 0;

  const storageColor = storagePercent >= 90 ? 'bg-rose-500' : storagePercent >= 75 ? 'bg-amber-500' : 'bg-emerald-500';
  const recordsColor = recordsPercent >= 90 ? 'bg-rose-500' : recordsPercent >= 75 ? 'bg-amber-500' : 'bg-indigo-500';

  if (compact) {
    return (
      <div className="space-y-1.5 min-w-[140px] text-[11px]">
        <div>
          <div className="flex items-center justify-between text-[10px] text-slate-500 dark:text-slate-400 mb-0.5">
            <span className="flex items-center gap-1"><HardDrive className="w-2.5 h-2.5" /> Tárhely</span>
            <span className="font-bold">{storagePercent}%</span>
          </div>
          <div className="w-full h-1.5 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
            <div className={`h-full rounded-full transition-all ${storageColor}`} style={{ width: `${storagePercent}%` }} />
          </div>
          <div className="text-[9px] text-slate-400 mt-0.5">{usedStorageMb.toFixed(1)} / {maxStorageMb || '∞'} MB{t.storage_source === 'global' ? ' · glob.' : ''}</div>
        </div>
        <div>
          <div className="flex items-center justify-between text-[10px] text-slate-500 dark:text-slate-400 mb-0.5">
            <span className="flex items-center gap-1"><Database className="w-2.5 h-2.5" /> Adatbázis</span>
            <span className="font-bold">{recordsPercent}%</span>
          </div>
          <div className="w-full h-1.5 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
            <div className={`h-full rounded-full transition-all ${recordsColor}`} style={{ width: `${recordsPercent}%` }} />
          </div>
          <div className="text-[9px] text-slate-400 mt-0.5">{usedRecords} / {maxRecords} db</div>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-3 p-3 bg-slate-50 dark:bg-slate-900/60 border border-slate-200 dark:border-slate-800 rounded-xl space-y-2.5">
      <div className="text-[10px] font-bold tracking-wider text-slate-400 dark:text-slate-500 uppercase flex items-center justify-between">
        <span>Erőforrás-kihasználtság</span>
        <span className="text-[9px] font-normal text-slate-400">Kvótakorlát</span>
      </div>
      <div>
        <div className="flex items-center justify-between text-xs mb-1">
          <span className="text-slate-600 dark:text-slate-400 flex items-center gap-1.5">
            <HardDrive className="w-3.5 h-3.5 text-slate-400" />
            Tárhely (R2)
          </span>
          <span className="font-bold text-slate-800 dark:text-slate-200">
            {usedStorageMb.toFixed(1)} / {maxStorageMb || '∞'} MB <span className="text-slate-400 font-normal">({storagePercent}%{t.storage_source === 'global' ? ', globális' : ''})</span>
          </span>
        </div>
        <div className="w-full h-2 bg-slate-200 dark:bg-slate-800 rounded-full overflow-hidden">
          <div className={`h-full rounded-full transition-all ${storageColor}`} style={{ width: `${storagePercent}%` }} />
        </div>
        {((t.trash_bytes || 0) > 0 || (t.archive_bytes || 0) > 0) && (
          <div className="text-[10px] text-slate-400 mt-0.5">ebből Lomtár {((t.trash_bytes || 0) / 1048576).toFixed(1)} MB, Archívum {((t.archive_bytes || 0) / 1048576).toFixed(1)} MB</div>
        )}
      </div>
      <div>
        <div className="flex items-center justify-between text-xs mb-1">
          <span className="text-slate-600 dark:text-slate-400 flex items-center gap-1.5">
            <Database className="w-3.5 h-3.5 text-slate-400" />
            Közzétételi tételek (D1)
          </span>
          <span className="font-bold text-slate-800 dark:text-slate-200">
            {usedRecords} / {maxRecords} db <span className="text-slate-400 font-normal">({recordsPercent}%)</span>
          </span>
        </div>
        <div className="w-full h-2 bg-slate-200 dark:bg-slate-800 rounded-full overflow-hidden">
          <div className={`h-full rounded-full transition-all ${recordsColor}`} style={{ width: `${recordsPercent}%` }} />
        </div>
      </div>
    </div>
  );
};

export const TenantsAdminView: React.FC = () => {
  const { role, user, impersonateTenant } = useAuth();
  const { setActiveCategory, setActiveModule } = useLayout();

  const [tenants, setTenants] = useState<Tenant[]>([]);
  // kvóták: globális alapérték (a szerkesztőben a „globális” jelölőhöz) és a Kvóták ablak
  const [quotaOpen, setQuotaOpen] = useState(false);
  const [quotaDefaults, setQuotaDefaults] = useState<QuotaDefaults | null>(null);
  const [quotaPending, setQuotaPending] = useState(0);
  const loadQuotaInfo = async () => {
    const r = await ApiClient.getAdminQuota().catch(() => null);
    if (r?.ok && r.data) { setQuotaDefaults(r.data.defaults); setQuotaPending(r.data.requests.filter((q) => q.status === 'pending').length); }
  };
  useEffect(() => { if (role === 'superadmin') loadQuotaInfo(); }, [role]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [viewMode, setViewMode] = usePersistedViewMode<ViewMode>('tenants', 'cards', ['cards', 'table', 'accordion']);
  const [expandedTenants, setExpandedTenants] = useState<Set<number>>(new Set());

  // Table sorting
  const [sortField, setSortField] = useState<keyof Tenant>('name');
  const [sortAsc, setSortAsc] = useState(true);

  // Modal
  const [modalOpen, setModalOpen] = useState(false);
  const [editingTenant, setEditingTenant] = useState<Partial<Tenant> | null>(null);
  const [exporting, setExporting] = useState(false);
  const [provisionModalOpen, setProvisionModalOpen] = useState(false);
  const [batchModalOpen, setBatchModalOpen] = useState(false);
  const [templateEditorOpen, setTemplateEditorOpen] = useState(false);
  // Új szervezetnél: a 2026-os közzétételi lista automatikus betöltése (alapból bekapcsolva)
  const [provisionOnCreate, setProvisionOnCreate] = useState(true);
  const [createNotice, setCreateNotice] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  // Szervezeti egyedi belépési link ablak
  const [loginLinkTenant, setLoginLinkTenant] = useState<Tenant | null>(null);
  const [targetTenantForProvision, setTargetTenantForProvision] = useState<Tenant | null>(null);
  const [downloadingTenantZip, setDownloadingTenantZip] = useState<number | null>(null);

  // Clone to Production Modal State
  const [cloneModalTenant, setCloneModalTenant] = useState<Tenant | null>(null);
  const [cloneName, setCloneName] = useState('');
  const [cloneSlug, setCloneSlug] = useState('');
  const [cloneCopyUsers, setCloneCopyUsers] = useState(true);
  const [cloning, setCloning] = useState(false);
  const [cloneStatusMsg, setCloneStatusMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Environment Diff Modal State (Superadmin only)
  const [diffModalOpen, setDiffModalOpen] = useState(false);

  const handleDownloadFolderZip = async (tenantId: number, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    setDownloadingTenantZip(tenantId);
    try {
      await ApiClient.downloadFolderTemplateZip({ tenantId, blank: false });
    } catch (err) {
      console.error('Mappastruktúra letöltési hiba:', err);
    } finally {
      setDownloadingTenantZip(null);
    }
  };

  const loadTenants = async () => {
    setLoading(true);
    const res = await ApiClient.getTenants();
    if (res.ok && res.data) {
      setTenants(res.data);
    }
    setLoading(false);
  };

  useEffect(() => {
    loadTenants();
  }, []);

  const handleOpenNew = () => {
    setEditingTenant({
      name: '',
      slug: '',
      short_name: '',
      address: '',
      email: '',
      phone: '',
      website: '',
      brand_color: '#4f46e5',
      brand_color2: '#06b6d4',
      active: 1,
      is_demo: 0,
      max_storage_mb: null,
      max_records: null,
    });
    setProvisionOnCreate(true);
    setModalOpen(true);
  };

  const handleOpenEdit = (t: Tenant) => {
    setEditingTenant({ ...t });
    setModalOpen(true);
  };

  const handleOpenCloneModal = (t: Tenant, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    const cleanName = (t.name || '').replace(/\s*\((?:DEMO|TESZT)\)/gi, '').replace(/^\[(?:DEMO|TESZT)\]\s*/gi, '').trim();
    const cleanSlug = (t.slug || '').replace(/-(?:demo|sandbox|teszt)$/gi, '').trim();
    setCloneModalTenant(t);
    setCloneName(cleanName || `${t.name} (Éles)`);
    setCloneSlug(cleanSlug || `${t.slug}-eles`);
    setCloneCopyUsers(true);
    setCloneStatusMsg(null);
  };

  const handleExecuteClone = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!cloneModalTenant) return;
    setCloning(true);
    setCloneStatusMsg(null);
    try {
      const res = await ApiClient.cloneTenantToProduction(cloneModalTenant.id, {
        name: cloneName,
        slug: cloneSlug,
        copy_users: cloneCopyUsers,
      });
      if (res.ok) {
        setCloneStatusMsg({
          type: 'success',
          text: res.message || `A(z) "${cloneName}" éles szervezet sikeresen létrejött!`,
        });
        await loadTenants();
        setTimeout(() => {
          setCloneModalTenant(null);
        }, 1800);
      } else {
        setCloneStatusMsg({
          type: 'error',
          text: res.error || 'Hiba történt a szervezet átmásolása során',
        });
      }
    } catch (err: any) {
      setCloneStatusMsg({
        type: 'error',
        text: err.message || 'Hálózati hiba a másolás során',
      });
    } finally {
      setCloning(false);
    }
  };

  const handlePromoteDirectly = async () => {
    if (!cloneModalTenant) return;
    if (!confirm(`Biztosan élesíti a(z) "${cloneModalTenant.name}" szervezetet? A DEMO jelölés megszűnik, és a hozzá tartozó felhasználók aktiválódnak.`)) return;
    setCloning(true);
    setCloneStatusMsg(null);
    try {
      const res = await ApiClient.promoteTenantToProduction(cloneModalTenant.id);
      if (res.ok) {
        setCloneStatusMsg({
          type: 'success',
          text: res.message || 'A szervezet sikeresen élesítve!',
        });
        await loadTenants();
        setTimeout(() => {
          setCloneModalTenant(null);
        }, 1500);
      } else {
        setCloneStatusMsg({
          type: 'error',
          text: res.error || 'Hiba történt az élesítés során',
        });
      }
    } catch (err: any) {
      setCloneStatusMsg({
        type: 'error',
        text: err.message || 'Hiba történt az élesítés során',
      });
    } finally {
      setCloning(false);
    }
  };

  const canDeleteTenant = (t: Tenant) => {
    // A rendszer alapértelmezett szervezete (ID: 1) soha nem törölhető
    if (t.id === 1) return false;
    // Saját aktív szervezetet nem lehet közvetlenül törölni
    if (t.id === user?.tenant_id) return false;

    if (role === 'superadmin' || role === 'tenant_admin') {
      return true;
    }

    if (role === 'org_admin') {
      // Szervezeti adminisztrátorként csak a próba/DEMO szervezet törölhető
      return t.is_demo === 1 || t.slug.endsWith('-demo') || t.slug.endsWith('-sandbox');
    }

    return false;
  };

  const handleDeleteTenant = async (t: Tenant, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    if (!canDeleteTenant(t)) return;

    const isDemo = t.is_demo === 1 || t.slug.endsWith('-demo') || t.slug.endsWith('-sandbox');
    const warningText = isDemo
      ? `Biztosan törölni szeretné a(z) "${t.name}" DEMO szervezetet?\n\nA szervezethez tartozó minden próbaadat, importált oldal, rekord és beállítás véglegesen törlődik.`
      : `Biztosan törölni szeretné a(z) "${t.name}" szervezetet?\n\nFIGYELEM: Ez a művelet végleges és nem vonható vissza! A szervezet összes oldala, rögzített adata és beállítása törlődik.`;

    if (!confirm(warningText)) return;

    try {
      const res = await ApiClient.deleteTenant(t.id);
      if (res.ok) {
        if (editingTenant?.id === t.id) {
          setModalOpen(false);
          setEditingTenant(null);
        }
        await loadTenants();
      } else {
        alert(res.error || 'Hiba történt a szervezet törlése során');
      }
    } catch (err: any) {
      alert(err?.message || 'Hálózati hiba a szervezet törlése során');
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingTenant || !editingTenant.name || !editingTenant.slug) return;

    if (editingTenant.id) {
      const res = await ApiClient.updateTenant(editingTenant.id, editingTenant);
      if (res.ok) {
        setModalOpen(false);
        loadTenants();
      } else {
        alert(res.error || 'Hiba a szervezet mentésekor');
      }
    } else {
      const res = await ApiClient.createTenant({ ...editingTenant, provision_template: provisionOnCreate });
      if (res.ok) {
        setModalOpen(false);
        const tpl = (res as any).template;
        if (provisionOnCreate && tpl) {
          setCreateNotice(tpl.ok
            ? { type: 'success', text: `„${editingTenant.name}” létrehozva, a Közzétételi lista 2026 betöltve (${tpl.records ?? 0} pont, ${tpl.pages ?? 0} oldal).` }
            : { type: 'error', text: `„${editingTenant.name}” létrehozva, de a sablon betöltése nem sikerült. A Sablon – batch módban pótolható.` });
        } else {
          setCreateNotice({ type: 'success', text: `„${editingTenant.name}” létrehozva.` });
        }
        loadTenants();
      } else {
        alert(res.error || 'Hiba a szervezet létrehozásakor');
      }
    }
  };

  const handleImpersonate = async (t: Tenant) => {
    if (!window.confirm(`Biztosan át szeretne lépni a(z) "${t.name}" profiljába megbízás alapján?`)) {
      return;
    }
    const res = await impersonateTenant(t.id);
    if (res.ok) {
      setActiveCategory('transparency_data');
      setActiveModule('records_manager');
    } else {
      alert(res.error || 'Hiba a szervezetbe való átlépéskor');
    }
  };

  const toggleAccordion = (id: number) => {
    setExpandedTenants((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSort = (field: keyof Tenant) => {
    if (sortField === field) {
      setSortAsc(!sortAsc);
    } else {
      setSortField(field);
      setSortAsc(true);
    }
  };

  const handleExportXLSX = async () => {
    setExporting(true);
    try {
      await downloadTenantsXLSX();
    } catch (err: any) {
      alert('Hiba a táblázat exportálásakor: ' + (err.message || err));
    }
    setExporting(false);
  };

  const handleExportPDF = async () => {
    setExporting(true);
    try {
      await downloadTenantsPDF(sortedTenants);
    } catch (err: any) {
      alert('Hiba a PDF exportálásakor: ' + (err.message || err));
    }
    setExporting(false);
  };

  const filtered = tenants.filter(t =>
    t.name.toLowerCase().includes(search.toLowerCase()) ||
    t.slug.toLowerCase().includes(search.toLowerCase()) ||
    (t.email && t.email.toLowerCase().includes(search.toLowerCase())) ||
    (t.short_name && t.short_name.toLowerCase().includes(search.toLowerCase()))
  );

  const sortedTenants = [...filtered].sort((a, b) => {
    const valA = a[sortField] ?? '';
    const valB = b[sortField] ?? '';
    if (valA < valB) return sortAsc ? -1 : 1;
    if (valA > valB) return sortAsc ? 1 : -1;
    return 0;
  });

  const canImpersonate = ['superadmin', 'tenant_admin'].includes(role);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-5 border-b border-slate-200 dark:border-slate-800">
        <div>
          <h1 className="text-2xl font-extrabold text-slate-900 dark:text-white flex items-center gap-2.5">
            <Building2 className="w-7 h-7 text-indigo-600 dark:text-indigo-400" />
            <span>Szervezetek & Intézmények (Tenants)</span>
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            Bérlők, önkormányzati szervek és intézmények felügyelete, nézetek és megbízás alapú átlépés
          </p>
        </div>

        <div className="flex items-center flex-wrap gap-2">
          {/* Export Buttons */}
          <button
            onClick={handleExportXLSX}
            disabled={exporting || loading}
            className="flex items-center gap-1.5 px-3 py-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-200 rounded-xl text-xs font-semibold transition cursor-pointer shadow-xs disabled:opacity-50"
            title="Szervezetek letöltése Excel (XLSX) táblázatban"
          >
            <FileSpreadsheet className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
            <span className="hidden md:inline">XLSX Export</span>
          </button>
          <button
            onClick={handleExportPDF}
            disabled={exporting || loading}
            className="flex items-center gap-1.5 px-3 py-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-200 rounded-xl text-xs font-semibold transition cursor-pointer shadow-xs disabled:opacity-50"
            title="Szervezetek letöltése PDF jelentésben"
          >
            <FileText className="w-4 h-4 text-rose-600 dark:text-rose-400" />
            <span className="hidden md:inline">PDF Export</span>
          </button>

          <button
            onClick={loadTenants}
            className="p-2.5 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-600 dark:text-slate-300 rounded-xl transition cursor-pointer shadow-xs"
            title="Frissítés"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>

          <button
            onClick={() => ApiClient.downloadDemoPackageZip()}
            className="flex items-center gap-2 px-3.5 py-2 bg-amber-50 hover:bg-amber-100 dark:bg-amber-950/40 dark:hover:bg-amber-900/40 text-amber-800 dark:text-amber-300 font-semibold rounded-xl text-xs border border-amber-300 dark:border-amber-800 shadow-xs transition cursor-pointer"
            title="5 éves (2022–2026), 4 intézményes komplett teszt- és demó adatcsomag letöltése (ZIP)"
          >
            <Package className="w-4 h-4 text-amber-600 dark:text-amber-400" />
            <span className="hidden sm:inline">5 Éves DEMO Csomag (ZIP)</span>
          </button>

          {/* Szuperadmin Környezeti Eltérés Vizsgáló Gomb */}
          {['superadmin', 'tenant_admin'].includes(role) && (
            <button
              onClick={() => setDiffModalOpen(true)}
              className="flex items-center gap-1.5 px-3 py-2 bg-gradient-to-r from-indigo-50 to-purple-50 hover:from-indigo-100 hover:to-purple-100 dark:from-indigo-950/50 dark:to-purple-950/50 dark:hover:from-indigo-900/50 dark:hover:to-purple-900/50 text-indigo-700 dark:text-indigo-300 font-semibold rounded-xl text-xs border border-indigo-200 dark:border-indigo-800 shadow-xs transition cursor-pointer"
              title="Helyi és éles rendszer szervezeteinek pontos összehasonlítása (16 vs 15 szervezet eltérésvizsgálat)"
            >
              <GitCompare className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
              <span className="hidden sm:inline">Környezet Eltérés (Local ↔ Éles)</span>
            </button>
          )}

          {role === 'superadmin' && (
            <button
              onClick={() => setQuotaOpen(true)}
              className="relative flex items-center gap-1.5 px-3 py-2 bg-white dark:bg-slate-900 border border-indigo-200 dark:border-indigo-800 hover:bg-indigo-50 dark:hover:bg-indigo-950/40 text-indigo-700 dark:text-indigo-300 rounded-xl text-xs font-semibold transition cursor-pointer shadow-xs"
              title="Globális kvóta-alapérték és a tárhelybővítési igények"
            >
              <HardDrive className="w-4 h-4" />
              <span className="hidden sm:inline">Kvóták</span>
              {quotaPending > 0 && <span className="absolute -top-1.5 -right-1.5 min-w-[1.1rem] h-[1.1rem] px-1 rounded-full bg-rose-600 text-white text-[10px] font-bold flex items-center justify-center" aria-label={`${quotaPending} elbírálásra váró igény`}>{quotaPending}</span>}
            </button>
          )}

          {role === 'superadmin' && (
            <button
              onClick={() => setBatchModalOpen(true)}
              className="flex items-center gap-1.5 px-3 py-2 bg-white dark:bg-slate-900 border border-indigo-200 dark:border-indigo-800 hover:bg-indigo-50 dark:hover:bg-indigo-950/40 text-indigo-700 dark:text-indigo-300 rounded-xl text-xs font-semibold transition cursor-pointer shadow-xs"
              title="Közzétételi lista 2026 betöltése, átállás vagy újraírás több szervezetre egyszerre"
            >
              <Layers className="w-4 h-4" />
              <span className="hidden sm:inline">Sablon – batch mód</span>
            </button>
          )}

          {role === 'superadmin' && (
            <button
              onClick={() => setTemplateEditorOpen(true)}
              className="flex items-center gap-1.5 px-3 py-2 bg-white dark:bg-slate-900 border border-indigo-200 dark:border-indigo-800 hover:bg-indigo-50 dark:hover:bg-indigo-950/40 text-indigo-700 dark:text-indigo-300 rounded-xl text-xs font-semibold transition cursor-pointer shadow-xs"
              title="A 2026-os közzétételi lista szövegének javítása (cím, leírás, frissítés, megőrzés), xlsx letöltés / visszatöltés"
            >
              <FilePenLine className="w-4 h-4" />
              <span className="hidden sm:inline">Sablon szerkesztése</span>
            </button>
          )}

          {['superadmin', 'tenant_admin'].includes(role) && (
            <button
              onClick={handleOpenNew}
              className="flex items-center gap-2 px-4 py-2.5 bg-indigo-600 hover:bg-indigo-500 text-white font-semibold rounded-xl text-sm shadow-md transition cursor-pointer"
            >
              <Plus className="w-4 h-4" />
              <span>Új Szervezet</span>
            </button>
          )}
        </div>
      </div>

      {createNotice && (
        <div role="status" className={`flex items-start justify-between gap-3 px-4 py-3 rounded-xl border text-sm ${createNotice.type === 'success' ? 'bg-emerald-50 border-emerald-200 text-emerald-800 dark:bg-emerald-950/30 dark:border-emerald-800 dark:text-emerald-300' : 'bg-rose-50 border-rose-200 text-rose-800 dark:bg-rose-950/30 dark:border-rose-800 dark:text-rose-300'}`}>
          <span>{createNotice.text}</span>
          <button type="button" onClick={() => setCreateNotice(null)} aria-label="Értesítés bezárása" className="text-xs font-semibold opacity-70 hover:opacity-100 cursor-pointer">✕</button>
        </div>
      )}

      {/* Filter and View Mode Switcher Toolbar */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Keresés név, slug, email vagy rövid név alapján..."
            className="w-full pl-10 pr-4 py-2 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-800 rounded-xl text-slate-900 dark:text-slate-100 text-sm placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 shadow-xs"
          />
        </div>

        <div className="flex items-center gap-3">
          {/* View Mode Toggle */}
          <div className="flex items-center bg-slate-100 dark:bg-slate-800 p-1 rounded-xl border border-slate-200 dark:border-slate-700">
            <button
              onClick={() => setViewMode('cards')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition cursor-pointer ${
                viewMode === 'cards'
                  ? 'bg-white dark:bg-slate-700 text-indigo-600 dark:text-indigo-400 shadow-xs'
                  : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
              }`}
              title="Kártyás nézet"
            >
              <LayoutGrid className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Kártyák</span>
            </button>
            <button
              onClick={() => setViewMode('table')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition cursor-pointer ${
                viewMode === 'table'
                  ? 'bg-white dark:bg-slate-700 text-indigo-600 dark:text-indigo-400 shadow-xs'
                  : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
              }`}
              title="Táblázatos nézet"
            >
              <TableIcon className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Táblázat</span>
            </button>
            <button
              onClick={() => setViewMode('accordion')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition cursor-pointer ${
                viewMode === 'accordion'
                  ? 'bg-white dark:bg-slate-700 text-indigo-600 dark:text-indigo-400 shadow-xs'
                  : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
              }`}
              title="Lenyíló lista nézet"
            >
              <ListCollapse className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Lenyíló lista</span>
            </button>
          </div>

          <div className="text-xs text-slate-500 whitespace-nowrap">
            Összesen: <span className="font-semibold text-slate-900 dark:text-white">{sortedTenants.length}</span> szervezet
          </div>
        </div>
      </div>

      {/* CONTENT BASED ON VIEW MODE */}
      {loading ? (
        <div className="py-16 text-center text-slate-500 bg-white dark:bg-slate-900/40 border border-slate-200 dark:border-slate-800 rounded-2xl">
          <RefreshCw className="w-6 h-6 animate-spin mx-auto text-indigo-500 mb-2" />
          Szervezetek betöltése...
        </div>
      ) : sortedTenants.length === 0 ? (
        <div className="py-12 text-center text-slate-500 bg-white dark:bg-slate-900/40 border border-slate-200 dark:border-slate-800 rounded-2xl">
          Nincs a keresésnek megfelelő szervezet.
        </div>
      ) : viewMode === 'cards' ? (
        /* ── 1. KÁRTYÁS NÉZET ── */
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {sortedTenants.map((t) => (
            <div
              key={t.id}
              className="bg-white dark:bg-slate-900/80 border border-slate-200 dark:border-slate-800 rounded-2xl p-5 shadow-xs flex flex-col justify-between hover:border-slate-300 dark:hover:border-slate-700 transition"
            >
              <div>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h3 className="text-base font-bold text-slate-900 dark:text-white">{t.name}</h3>
                    <div className="text-xs font-mono text-indigo-600 dark:text-indigo-400 mt-0.5">/{t.slug}</div>
                  </div>
                  <div className="flex items-center gap-1.5">
                    {t.is_demo === 1 && (
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-bold border bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/30 flex items-center gap-1 shadow-xs" title="Teszt / Betanulási szervezet">
                        <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />
                        DEMO
                      </span>
                    )}
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold border ${
                      t.active ? 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-800' : 'bg-slate-100 text-slate-500 border-slate-200 dark:bg-slate-800 dark:text-slate-400'
                    }`}>
                      {t.active ? 'Aktív' : 'Inaktív'}
                    </span>
                  </div>
                </div>

                <div className="mt-4 space-y-1.5 text-xs text-slate-600 dark:text-slate-400">
                  {t.short_name && <div><span className="text-slate-400 font-medium">Rövid név:</span> {t.short_name}</div>}
                  {t.address && <div><span className="text-slate-400 font-medium">Székhely:</span> {t.address}</div>}
                  {t.email && (
                    <div className="flex items-center gap-1.5 truncate">
                      <Mail className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                      <span className="truncate">{t.email}</span>
                    </div>
                  )}
                  {t.phone && (
                    <div className="flex items-center gap-1.5">
                      <Phone className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                      <span>{t.phone}</span>
                    </div>
                  )}
                  {t.website && (
                    <div className="flex items-center gap-1.5 truncate">
                      <Globe className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                      <a href={t.website} target="_blank" rel="noreferrer" className="text-indigo-600 dark:text-indigo-400 hover:underline flex items-center gap-1 truncate">
                        <span className="truncate">{t.website}</span>
                        <ExternalLink className="w-3 h-3 shrink-0" />
                      </a>
                    </div>
                  )}
                </div>

                {/* Quota Usage Bars */}
                {renderQuotaUsage(t)}
              </div>

              <div className="mt-5 pt-3 border-t border-slate-100 dark:border-slate-800 flex flex-wrap items-center justify-between gap-2">
                {canImpersonate && (
                  <div className="flex items-center gap-1.5">
                    <button
                      onClick={() => handleImpersonate(t)}
                      className="flex items-center gap-1 px-2.5 py-1.5 bg-amber-500/10 hover:bg-amber-500/20 text-amber-700 dark:text-amber-400 border border-amber-500/30 rounded-lg text-xs font-semibold transition cursor-pointer shrink-0"
                      title="Átlépés a szervezet felületére megbízás alapján"
                    >
                      <UserCheck className="w-3.5 h-3.5" />
                      <span>Átlépés</span>
                    </button>
                  </div>
                )}

                <div className="flex flex-wrap items-center gap-1.5 ml-auto">
                  <button
                    onClick={(e) => handleDownloadFolderZip(t.id, e)}
                    disabled={downloadingTenantZip === t.id}
                    className="flex items-center gap-1 px-2 py-1.5 bg-blue-500/10 hover:bg-blue-500/20 text-blue-700 dark:text-blue-400 border border-blue-500/30 rounded-lg text-xs font-medium transition cursor-pointer shrink-0"
                    title="Mappastruktúra letöltése ZIP-ben (kitöltéshez és fájlok beillesztéséhez)"
                  >
                    {downloadingTenantZip === t.id ? (
                      <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <FolderDown className="w-3.5 h-3.5" />
                    )}
                    <span>Mappák</span>
                  </button>
                  <button
                    onClick={() => {
                      setTargetTenantForProvision(t);
                      setProvisionModalOpen(true);
                    }}
                    className="flex items-center gap-1 px-2 py-1.5 bg-indigo-500/10 hover:bg-indigo-500/20 text-indigo-700 dark:text-indigo-400 border border-indigo-500/30 rounded-lg text-xs font-medium transition cursor-pointer shrink-0"
                    title="Sablon-változók (környezeti változók, JSON) ennél a szervezetnél"
                  >
                    <Sparkles className="w-3.5 h-3.5" />
                    <span>Sablon</span>
                  </button>
                  {t.brand_color && (
                    <span className="w-3.5 h-3.5 rounded-full border border-slate-200 dark:border-slate-700 shrink-0" style={{ backgroundColor: t.brand_color }} title="Arculati alapszín"></span>
                  )}
                  <button
                    onClick={() => setLoginLinkTenant(t)}
                    className="flex items-center gap-1 px-2.5 py-1.5 bg-violet-500/10 hover:bg-violet-500/20 text-violet-700 dark:text-violet-400 border border-violet-500/30 rounded-lg text-xs font-semibold transition cursor-pointer shrink-0"
                    title="A szervezet egyedi belépési linkje"
                  >
                    <KeyRound className="w-3.5 h-3.5" />
                    <span className="hidden sm:inline">Belépési link</span>
                  </button>
                  {t.is_demo === 1 && (
                    <button
                      onClick={(e) => handleOpenCloneModal(t, e)}
                      className="flex items-center gap-1 px-2.5 py-1.5 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-700 dark:text-emerald-400 border border-emerald-500/30 rounded-lg text-xs font-semibold transition cursor-pointer shrink-0"
                      title="DEMO adatok átmásolása új éles szervezetként"
                    >
                      <Rocket className="w-3.5 h-3.5 text-emerald-500" />
                      <span>Élesítés</span>
                    </button>
                  )}
                  <button
                    onClick={() => handleOpenEdit(t)}
                    className="flex items-center gap-1 px-2.5 py-1.5 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 rounded-lg text-xs font-semibold transition cursor-pointer shrink-0"
                  >
                    <Edit2 className="w-3.5 h-3.5" />
                    <span>Szerkesztés</span>
                  </button>
                  {canDeleteTenant(t) && (
                    <button
                      onClick={(e) => handleDeleteTenant(t, e)}
                      className="flex items-center gap-1 px-2 py-1.5 bg-rose-50 hover:bg-rose-100 dark:bg-rose-950/40 dark:hover:bg-rose-900/60 text-rose-600 dark:text-rose-400 border border-rose-200 dark:border-rose-800/60 rounded-lg text-xs font-semibold transition cursor-pointer shrink-0"
                      title={t.is_demo ? "DEMO szervezet törlése" : "Szervezet törlése"}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                      <span>Törlés</span>
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : viewMode === 'table' ? (
        /* ── 2. TÁBLÁZATOS NÉZET ── */
        <div className="bg-white dark:bg-slate-900/70 border border-slate-200 dark:border-slate-800 rounded-2xl overflow-hidden shadow-xs">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="bg-slate-50 dark:bg-slate-950/70 border-b border-slate-200 dark:border-slate-800 text-xs text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                <tr>
                  <th 
                    onClick={() => handleSort('name')} 
                    className="px-5 py-3.5 font-semibold cursor-pointer hover:text-slate-900 dark:hover:text-white select-none"
                  >
                    <div className="flex items-center gap-1.5">
                      <span>Szervezet Neve</span>
                      <ArrowUpDown className="w-3 h-3 opacity-60" />
                    </div>
                  </th>
                  <th 
                    onClick={() => handleSort('slug')} 
                    className="px-4 py-3.5 font-semibold cursor-pointer hover:text-slate-900 dark:hover:text-white select-none"
                  >
                    <div className="flex items-center gap-1.5">
                      <span>Slug</span>
                      <ArrowUpDown className="w-3 h-3 opacity-60" />
                    </div>
                  </th>
                  <th className="px-4 py-3.5 font-semibold">Kapcsolat & Weboldal</th>
                  <th className="px-4 py-3.5 font-semibold">Székhely</th>
                  <th className="px-4 py-3.5 font-semibold">Kvóta & Kihasználtság</th>
                  <th 
                    onClick={() => handleSort('active')} 
                    className="px-4 py-3.5 font-semibold cursor-pointer hover:text-slate-900 dark:hover:text-white select-none"
                  >
                    <div className="flex items-center gap-1.5">
                      <span>Státusz</span>
                      <ArrowUpDown className="w-3 h-3 opacity-60" />
                    </div>
                  </th>
                  <th className="px-5 py-3.5 text-right font-semibold">Műveletek</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 dark:divide-slate-800/60">
                {sortedTenants.map((t) => (
                  <tr key={t.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-colors">
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-slate-900 dark:text-white">{t.name}</span>
                        {t.is_demo === 1 && (
                          <span className="px-1.5 py-0.5 rounded text-[10px] font-bold border bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/30 inline-flex items-center gap-1 shadow-xs" title="Teszt / Betanulási szervezet">
                            <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />
                            DEMO
                          </span>
                        )}
                      </div>
                      {t.short_name && <div className="text-xs text-slate-500 dark:text-slate-400">Rövid név: {t.short_name}</div>}
                    </td>
                    <td className="px-4 py-4 font-mono text-xs text-indigo-600 dark:text-indigo-400">
                      /{t.slug}
                    </td>
                    <td className="px-4 py-4 text-xs text-slate-600 dark:text-slate-400 space-y-1">
                      {t.email && (
                        <div className="flex items-center gap-1.5 truncate max-w-xs">
                          <Mail className="w-3 h-3 text-slate-400 shrink-0" />
                          <span className="truncate">{t.email}</span>
                        </div>
                      )}
                      {t.phone && (
                        <div className="flex items-center gap-1.5">
                          <Phone className="w-3 h-3 text-slate-400 shrink-0" />
                          <span>{t.phone}</span>
                        </div>
                      )}
                      {t.website && (
                        <div className="flex items-center gap-1.5 truncate max-w-xs">
                          <Globe className="w-3 h-3 text-slate-400 shrink-0" />
                          <a href={t.website} target="_blank" rel="noreferrer" className="text-indigo-600 dark:text-indigo-400 hover:underline truncate">
                            {t.website}
                          </a>
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-4 text-xs text-slate-600 dark:text-slate-400 max-w-xs">
                      {t.address || '–'}
                    </td>
                    <td className="px-4 py-4">
                      {renderQuotaUsage(t, true)}
                    </td>
                    <td className="px-4 py-4">
                      <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold border ${
                        t.active ? 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-800' : 'bg-slate-100 text-slate-500 border-slate-200 dark:bg-slate-800 dark:text-slate-400'
                      }`}>
                        {t.active ? 'Aktív' : 'Inaktív'}
                      </span>
                    </td>
                    <td className="px-5 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        {canImpersonate && (
                          <>
                            <button
                              onClick={() => handleImpersonate(t)}
                              className="flex items-center gap-1 px-2.5 py-1.5 bg-amber-500/10 hover:bg-amber-500/20 text-amber-700 dark:text-amber-400 border border-amber-500/30 rounded-lg text-xs font-semibold transition cursor-pointer"
                              title="Átlépés a szervezet felületére megbízás alapján"
                            >
                              <UserCheck className="w-3.5 h-3.5" />
                              <span>Átlépés</span>
                            </button>
                          </>
                        )}
                        <button
                          onClick={(e) => handleDownloadFolderZip(t.id, e)}
                          disabled={downloadingTenantZip === t.id}
                          className="flex items-center gap-1 px-2.5 py-1.5 bg-blue-500/10 hover:bg-blue-500/20 text-blue-700 dark:text-blue-400 border border-blue-500/30 rounded-lg text-xs font-semibold transition cursor-pointer"
                          title="Mappastruktúra letöltése ZIP-ben"
                        >
                          {downloadingTenantZip === t.id ? (
                            <RefreshCw className="w-3 h-3 animate-spin" />
                          ) : (
                            <FolderDown className="w-3 h-3" />
                          )}
                          <span>Mappák</span>
                        </button>
                        <button
                          onClick={() => {
                            setTargetTenantForProvision(t);
                            setProvisionModalOpen(true);
                          }}
                          className="flex items-center gap-1 px-2.5 py-1.5 bg-indigo-500/10 hover:bg-indigo-500/20 text-indigo-700 dark:text-indigo-400 border border-indigo-500/30 rounded-lg text-xs font-semibold transition cursor-pointer"
                          title="Sablon-változók (környezeti változók, JSON)"
                        >
                          <Sparkles className="w-3 h-3" />
                          <span>Sablon</span>
                        </button>
                        <button
                          onClick={() => setLoginLinkTenant(t)}
                          className="flex items-center gap-1 px-2.5 py-1.5 bg-violet-500/10 hover:bg-violet-500/20 text-violet-700 dark:text-violet-400 border border-violet-500/30 rounded-lg text-xs font-semibold transition cursor-pointer shrink-0"
                          title="A szervezet egyedi belépési linkje"
                        >
                          <KeyRound className="w-3.5 h-3.5" />
                          <span className="hidden sm:inline">Belépési link</span>
                        </button>
                        {t.is_demo === 1 && (
                          <button
                            onClick={(e) => handleOpenCloneModal(t, e)}
                            className="flex items-center gap-1 px-2.5 py-1.5 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-700 dark:text-emerald-400 border border-emerald-500/30 rounded-lg text-xs font-semibold transition cursor-pointer shrink-0"
                            title="DEMO adatok átmásolása új éles szervezetként"
                          >
                            <Rocket className="w-3 h-3 text-emerald-500" />
                            <span>Élesítés</span>
                          </button>
                        )}
                        <button
                          onClick={() => handleOpenEdit(t)}
                          className="p-1.5 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-lg transition cursor-pointer"
                          title="Szerkesztés"
                        >
                          <Edit2 className="w-3.5 h-3.5" />
                        </button>
                        {canDeleteTenant(t) && (
                          <button
                            onClick={(e) => handleDeleteTenant(t, e)}
                            className="p-1.5 bg-rose-50 hover:bg-rose-100 dark:bg-rose-950/40 dark:hover:bg-rose-900/60 text-rose-600 dark:text-rose-400 border border-rose-200 dark:border-rose-800/60 rounded-lg transition cursor-pointer"
                            title={t.is_demo ? "DEMO szervezet törlése" : "Szervezet törlése"}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        /* ── 3. LENYÍLÓ / KINYÍLÓ LISTA (ACCORDION) NÉZET ── */
        <div className="space-y-2.5">
          {sortedTenants.map((t) => {
            const isExpanded = expandedTenants.has(t.id);
            return (
              <div
                key={t.id}
                className="bg-white dark:bg-slate-900/80 border border-slate-200 dark:border-slate-800 rounded-2xl overflow-hidden shadow-xs transition"
              >
                {/* Header bar */}
                <div 
                  onClick={() => toggleAccordion(t.id)}
                  className="p-4 sm:px-6 flex items-center justify-between gap-4 cursor-pointer hover:bg-slate-50/70 dark:hover:bg-slate-800/40 select-none"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <button className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200">
                      {isExpanded ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
                    </button>
                    {t.brand_color && (
                      <span className="w-3.5 h-3.5 rounded-full border border-slate-200 dark:border-slate-700 shrink-0" style={{ backgroundColor: t.brand_color }}></span>
                    )}
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-base font-bold text-slate-900 dark:text-white truncate">{t.name}</span>
                        {t.is_demo === 1 && (
                          <span className="px-1.5 py-0.5 rounded text-[10px] font-bold border bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/30 inline-flex items-center gap-1 shadow-xs" title="Teszt / Betanulási szervezet">
                            <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />
                            DEMO
                          </span>
                        )}
                        <span className="text-xs font-mono px-2 py-0.5 rounded bg-slate-100 dark:bg-slate-800 text-indigo-600 dark:text-indigo-400 font-semibold border border-slate-200 dark:border-slate-700">/{t.slug}</span>
                        {t.short_name && (
                          <span className="text-xs text-slate-500 font-medium">({t.short_name})</span>
                        )}
                      </div>
                      {t.address && (
                        <div className="text-xs text-slate-500 flex items-center gap-1 mt-0.5 truncate">
                          <MapPin className="w-3 h-3 shrink-0" />
                          <span className="truncate">{t.address}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-3 shrink-0" onClick={(e) => e.stopPropagation()}>
                    <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-semibold border ${
                      t.active ? 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-800' : 'bg-slate-100 text-slate-500 border-slate-200 dark:bg-slate-800 dark:text-slate-400'
                    }`}>
                      {t.active ? 'Aktív' : 'Inaktív'}
                    </span>

                    {canImpersonate && (
                      <>
                        <button
                          onClick={() => handleImpersonate(t)}
                          className="flex items-center gap-1.5 px-2.5 py-1.5 bg-amber-500/10 hover:bg-amber-500/20 text-amber-700 dark:text-amber-400 border border-amber-500/30 rounded-lg text-xs font-semibold transition cursor-pointer"
                          title="Átlépés a szervezet profiljába megbízás alapján"
                        >
                          <UserCheck className="w-3.5 h-3.5" />
                          <span className="hidden sm:inline">Átlépés</span>
                        </button>
                      </>
                    )}

                    {/* a többi nézettel egyezően: mappastruktúra és sablon (2026-os lista, átállás) */}
                    <button
                      onClick={(e) => handleDownloadFolderZip(t.id, e)}
                      disabled={downloadingTenantZip === t.id}
                      className="flex items-center gap-1 px-2.5 py-1.5 bg-blue-500/10 hover:bg-blue-500/20 text-blue-700 dark:text-blue-400 border border-blue-500/30 rounded-lg text-xs font-semibold transition cursor-pointer shrink-0"
                      title="Mappastruktúra letöltése ZIP-ben"
                    >
                      {downloadingTenantZip === t.id ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <FolderDown className="w-3.5 h-3.5" />}
                      <span className="hidden sm:inline">Mappák</span>
                    </button>
                    <button
                      onClick={() => {
                        setTargetTenantForProvision(t);
                        setProvisionModalOpen(true);
                      }}
                      className="flex items-center gap-1 px-2.5 py-1.5 bg-indigo-500/10 hover:bg-indigo-500/20 text-indigo-700 dark:text-indigo-400 border border-indigo-500/30 rounded-lg text-xs font-semibold transition cursor-pointer shrink-0"
                      title="Sablon-változók (környezeti változók, JSON)"
                    >
                      <Sparkles className="w-3.5 h-3.5" />
                      <span className="hidden sm:inline">Sablon</span>
                    </button>
                    <button
                      onClick={() => setLoginLinkTenant(t)}
                      className="flex items-center gap-1 px-2.5 py-1.5 bg-violet-500/10 hover:bg-violet-500/20 text-violet-700 dark:text-violet-400 border border-violet-500/30 rounded-lg text-xs font-semibold transition cursor-pointer shrink-0"
                      title="A szervezet egyedi belépési linkje"
                    >
                      <KeyRound className="w-3.5 h-3.5" />
                      <span className="hidden sm:inline">Belépési link</span>
                    </button>
                    {t.is_demo === 1 && (
                      <button
                        onClick={(e) => handleOpenCloneModal(t, e)}
                        className="flex items-center gap-1 px-2.5 py-1.5 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-700 dark:text-emerald-400 border border-emerald-500/30 rounded-lg text-xs font-semibold transition cursor-pointer shrink-0"
                        title="DEMO adatok átmásolása új éles szervezetként"
                      >
                        <Rocket className="w-3 h-3 text-emerald-500" />
                        <span className="hidden sm:inline">Élesítés</span>
                      </button>
                    )}
                    <button
                      onClick={() => handleOpenEdit(t)}
                      className="p-1.5 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-lg transition cursor-pointer"
                      title="Szerkesztés"
                    >
                      <Edit2 className="w-3.5 h-3.5" />
                    </button>

                    {canDeleteTenant(t) && (
                      <button
                        onClick={(e) => handleDeleteTenant(t, e)}
                        className="p-1.5 bg-rose-50 hover:bg-rose-100 dark:bg-rose-950/40 dark:hover:bg-rose-900/60 text-rose-600 dark:text-rose-400 border border-rose-200 dark:border-rose-800/60 rounded-lg transition cursor-pointer"
                        title={t.is_demo ? "DEMO szervezet törlése" : "Szervezet törlése"}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                </div>

                {/* Accordion Details Body */}
                {isExpanded && (
                  <div className="px-6 pb-5 pt-3 border-t border-slate-100 dark:border-slate-800/80 bg-slate-50/50 dark:bg-slate-950/40 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 text-xs">
                    <div className="space-y-1.5">
                      <div className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">Elérhetőségek</div>
                      <div className="text-slate-700 dark:text-slate-300 flex items-center gap-2">
                        <Mail className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                        <span className="truncate">{t.email || 'Nincs megadva'}</span>
                      </div>
                      <div className="text-slate-700 dark:text-slate-300 flex items-center gap-2">
                        <Phone className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                        <span>{t.phone || 'Nincs megadva'}</span>
                      </div>
                      {t.website && (
                        <div className="flex items-center gap-2">
                          <Globe className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                          <a href={t.website} target="_blank" rel="noreferrer" className="text-indigo-600 dark:text-indigo-400 hover:underline flex items-center gap-1">
                            <span className="truncate">{t.website}</span>
                            <ExternalLink className="w-3 h-3 shrink-0" />
                          </a>
                        </div>
                      )}
                    </div>

                    <div className="space-y-1.5">
                      <div className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">Hivatalos Cím</div>
                      <div className="text-slate-700 dark:text-slate-300 flex items-start gap-2">
                        <MapPin className="w-3.5 h-3.5 text-slate-400 shrink-0 mt-0.5" />
                        <span>{t.address || 'Nincs székhely cím rögzítve'}</span>
                      </div>
                      <div className="mt-2 flex items-center gap-2">
                        <span className="text-slate-400">Arculati szín:</span>
                        <div className="flex items-center gap-1.5">
                          <span className="w-3.5 h-3.5 rounded-full border border-slate-300" style={{ backgroundColor: t.brand_color || '#4f46e5' }}></span>
                          <span className="font-mono text-[11px] text-slate-500">{t.brand_color || '#4f46e5'}</span>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-1.5 flex flex-col justify-between">
                      <div>
                        <div className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">Publikus Portál Link</div>
                        <a 
                          href={`/public?t=${encodeURIComponent(t.slug)}`}
                          target="_blank" 
                          rel="noreferrer" 
                          className="inline-flex items-center gap-1 text-indigo-600 dark:text-indigo-400 hover:underline mt-1 font-semibold"
                        >
                          <span>Látogatói közzétételi oldal megnyitása</span>
                          <ExternalLink className="w-3.5 h-3.5" />
                        </a>
                      </div>

                      <div className="pt-2 flex items-center gap-2">
                        {canImpersonate && (
                          <button
                            onClick={() => handleImpersonate(t)}
                            className="w-full flex items-center justify-center gap-1.5 px-3 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-xl font-semibold shadow-xs transition cursor-pointer"
                          >
                            <UserCheck className="w-4 h-4" />
                            <span>Átlépés ebbe a profilba</span>
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Quota & Capacity Usage */}
                    <div className="sm:col-span-2 md:col-span-3 pt-2 border-t border-slate-200/60 dark:border-slate-800/60">
                      {renderQuotaUsage(t)}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Edit / New Modal */}
      {modalOpen && editingTenant && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-black/60 backdrop-blur-xs overflow-y-auto">
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl w-full max-w-lg p-5 sm:p-6 shadow-2xl my-auto max-h-[90vh] flex flex-col">
            <h2 className="text-lg font-bold text-slate-900 dark:text-white mb-4 shrink-0">
              {editingTenant.id ? 'Szervezet Módosítása' : 'Új Szervezet Létrehozása'}
            </h2>
            <form onSubmit={handleSave} className="space-y-4 overflow-y-auto pr-1 flex-1">
              <div>
                <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">Hivatalos Megnevezés *</label>
                <input
                  type="text"
                  required
                  value={editingTenant.name || ''}
                  onChange={(e) => setEditingTenant({ ...editingTenant, name: e.target.value })}
                  placeholder="pl. Minta Város Önkormányzata"
                  className="w-full px-3.5 py-2 bg-slate-50 dark:bg-slate-950 border border-slate-300 dark:border-slate-700 rounded-xl text-slate-900 dark:text-slate-100 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">URL Azonosító (Slug) *</label>
                  <input
                    type="text"
                    required
                    value={editingTenant.slug || ''}
                    onChange={(e) => setEditingTenant({ ...editingTenant, slug: e.target.value })}
                    placeholder="pl. minta-varos"
                    className="w-full px-3.5 py-2 bg-slate-50 dark:bg-slate-950 border border-slate-300 dark:border-slate-700 rounded-xl text-slate-900 dark:text-slate-100 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 font-mono"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">Rövidített Név</label>
                  <input
                    type="text"
                    value={editingTenant.short_name || ''}
                    onChange={(e) => setEditingTenant({ ...editingTenant, short_name: e.target.value })}
                    placeholder="pl. MVÖ"
                    className="w-full px-3.5 py-2 bg-slate-50 dark:bg-slate-950 border border-slate-300 dark:border-slate-700 rounded-xl text-slate-900 dark:text-slate-100 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">Székhely Cím</label>
                <input
                  type="text"
                  value={editingTenant.address || ''}
                  onChange={(e) => setEditingTenant({ ...editingTenant, address: e.target.value })}
                  placeholder="1234 Budapest, Fő utca 1."
                  className="w-full px-3.5 py-2 bg-slate-50 dark:bg-slate-950 border border-slate-300 dark:border-slate-700 rounded-xl text-slate-900 dark:text-slate-100 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">Központi E-mail</label>
                  <input
                    type="email"
                    value={editingTenant.email || ''}
                    onChange={(e) => setEditingTenant({ ...editingTenant, email: e.target.value })}
                    placeholder="hivatal@onkormanyzat.hu"
                    className="w-full px-3.5 py-2 bg-slate-50 dark:bg-slate-950 border border-slate-300 dark:border-slate-700 rounded-xl text-slate-900 dark:text-slate-100 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
                <div>
                  <label htmlFor="tenant-phone" className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">Központi Telefonszám</label>
                  <PhoneInput
                    key={editingTenant.id ?? 'new'}
                    id="tenant-phone"
                    value={editingTenant.phone}
                    onChange={(v) => setEditingTenant({ ...editingTenant, phone: v })}
                    className="w-full px-3.5 py-2 bg-slate-50 dark:bg-slate-950 border border-slate-300 dark:border-slate-700 rounded-xl text-slate-900 dark:text-slate-100 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">Weboldal</label>
                <input
                  type="url"
                  value={editingTenant.website || ''}
                  onChange={(e) => setEditingTenant({ ...editingTenant, website: e.target.value })}
                  placeholder="https://onkormanyzat.hu"
                  className="w-full px-3.5 py-2 bg-slate-50 dark:bg-slate-950 border border-slate-300 dark:border-slate-700 rounded-xl text-slate-900 dark:text-slate-100 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              <div className="p-3 bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-900/40 rounded-xl">
                <label className="flex items-start gap-3 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={editingTenant.is_demo === 1}
                    onChange={(e) => setEditingTenant({ ...editingTenant, is_demo: e.target.checked ? 1 : 0 })}
                    className="mt-0.5 w-4 h-4 rounded text-amber-600 focus:ring-amber-500"
                  />
                  <div>
                    <span className="text-xs font-bold text-amber-800 dark:text-amber-300 flex items-center gap-1.5">
                      <span className="w-2 h-2 rounded-full bg-amber-500 inline-block" />
                      DEMO / Betanulási tesztszervezet
                    </span>
                    <p className="text-[11px] text-amber-700/80 dark:text-amber-400/80 mt-0.5 leading-snug">
                      Jelölősemafor és figyelmeztetés jelenik meg az oldalakon és exportoknál, hogy az adatok nem élesek, kizárólag oktatási célokat szolgálnak.
                    </p>
                  </div>
                </label>
              </div>

              {!editingTenant.id && (
                <div className="p-3 bg-indigo-50/60 dark:bg-indigo-950/30 border border-indigo-200 dark:border-indigo-900/40 rounded-xl">
                  <label className="flex items-start gap-3 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={provisionOnCreate}
                      onChange={(e) => setProvisionOnCreate(e.target.checked)}
                      className="mt-0.5 w-4 h-4 rounded text-indigo-600 focus:ring-indigo-500"
                    />
                    <div>
                      <span className="text-xs font-bold text-indigo-900 dark:text-indigo-200">Közzétételi lista 2026 betöltése</span>
                      <p className="text-[11px] text-indigo-800/80 dark:text-indigo-300/80 mt-0.5 leading-snug">
                        Létrehozás után automatikusan betöltődik a „Közzétételi lista 2026 – Infotv. 1. melléklet (új lista)” sablon (teljes újraírás), a szervezet alapadataiból kitöltött változókkal.
                      </p>
                    </div>
                  </label>
                </div>
              )}

              {/* Erőforrás- és Kvótaszabályozás (Resource Quotas) */}
              <div className="p-3.5 bg-indigo-50/60 dark:bg-indigo-950/30 border border-indigo-200 dark:border-indigo-900/40 rounded-xl space-y-3">
                <div className="flex items-center gap-2">
                  <Database className="w-4 h-4 text-indigo-600 dark:text-indigo-400 shrink-0" />
                  <span className="text-xs font-bold text-indigo-900 dark:text-indigo-200">
                    Erőforrás- és Kvótaszabályozás
                  </span>
                </div>
                <p className="text-[11px] text-slate-600 dark:text-slate-400 leading-snug">
                  Szabályozza a szervezet felhasználói által maximálisan lefoglalható tárhelyet (feltöltött fájlok) és közzétételi rekordok számát a rendszer védelmére.
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">
                      Max. Tárhely (MB)
                    </label>
                    <input
                      type="number"
                      step={1}
                      /* B8: step=50 a min=10-től számított (10, 60, …, 460, 510), így az 500-as
                         alapérték érvénytelen volt és a böngésző nem engedte menteni az űrlapot. */
                      min={0}
                      disabled={editingTenant.max_storage_mb === null || editingTenant.max_storage_mb === undefined}
                      value={editingTenant.max_storage_mb ?? (quotaDefaults?.max_storage_mb ?? 500)}
                      onChange={(e) => setEditingTenant({ ...editingTenant, max_storage_mb: Math.max(0, parseInt(e.target.value) || 0) })}
                      className="w-full px-3 py-1.5 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded-lg text-slate-900 dark:text-slate-100 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-60"
                    />
                    <label className="mt-1 flex items-center gap-1.5 text-[11px] text-slate-600 dark:text-slate-400 cursor-pointer">
                      <input type="checkbox" checked={editingTenant.max_storage_mb === null || editingTenant.max_storage_mb === undefined}
                        onChange={(e) => setEditingTenant({ ...editingTenant, max_storage_mb: e.target.checked ? null : (quotaDefaults?.max_storage_mb ?? 500) })} />
                      Globális alapérték ({quotaDefaults ? (quotaDefaults.max_storage_mb ? `${quotaDefaults.max_storage_mb} MB` : 'korlátlan') : '…'}) · 0 = korlátlan
                    </label>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">
                      Max. Rekordok száma (db)
                    </label>
                    <input
                      type="number"
                      step={1}
                      /* B8: step=100 a min=50-től számított (…, 1950, 2050), így a 2000-es alapérték érvénytelen volt. */
                      min={0}
                      disabled={editingTenant.max_records === null || editingTenant.max_records === undefined}
                      value={editingTenant.max_records ?? (quotaDefaults?.max_records ?? 2000)}
                      onChange={(e) => setEditingTenant({ ...editingTenant, max_records: Math.max(0, parseInt(e.target.value) || 0) })}
                      className="w-full px-3 py-1.5 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded-lg text-slate-900 dark:text-slate-100 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-60"
                    />
                    <label className="mt-1 flex items-center gap-1.5 text-[11px] text-slate-600 dark:text-slate-400 cursor-pointer">
                      <input type="checkbox" checked={editingTenant.max_records === null || editingTenant.max_records === undefined}
                        onChange={(e) => setEditingTenant({ ...editingTenant, max_records: e.target.checked ? null : (quotaDefaults?.max_records ?? 2000) })} />
                      Globális alapérték ({quotaDefaults ? (quotaDefaults.max_records ? `${quotaDefaults.max_records} db` : 'korlátlan') : '…'}) · 0 = korlátlan
                    </label>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between pt-4 border-t border-slate-200 dark:border-slate-800">
                <div className="flex items-center gap-2">
                  {editingTenant?.id && canDeleteTenant(editingTenant as Tenant) && (
                    <button
                      type="button"
                      onClick={() => handleDeleteTenant(editingTenant as Tenant)}
                      className="flex items-center gap-1.5 px-3 py-2 bg-rose-50 hover:bg-rose-100 dark:bg-rose-950/40 dark:hover:bg-rose-900/60 text-rose-600 dark:text-rose-400 border border-rose-200 dark:border-rose-800/60 rounded-xl text-sm font-medium transition cursor-pointer"
                    >
                      <Trash2 className="w-4 h-4" />
                      <span>{editingTenant.is_demo ? 'DEMO törlése' : 'Szervezet törlése'}</span>
                    </button>
                  )}
                  {editingTenant?.id && editingTenant.is_demo === 1 && (
                    <button
                      type="button"
                      onClick={() => {
                        const t = tenants.find(x => x.id === editingTenant.id) || (editingTenant as Tenant);
                        setModalOpen(false);
                        handleOpenCloneModal(t);
                      }}
                      className="flex items-center gap-1.5 px-3 py-2 bg-emerald-50 hover:bg-emerald-100 dark:bg-emerald-950/40 dark:hover:bg-emerald-900/60 text-emerald-700 dark:text-emerald-400 border border-emerald-300 dark:border-emerald-800/60 rounded-xl text-sm font-semibold transition cursor-pointer"
                      title="DEMO adatok átmásolása új hivatalos éles szervezetként"
                    >
                      <Rocket className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                      <span>Átmásolás élesbe</span>
                    </button>
                  )}
                </div>
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => setModalOpen(false)}
                    className="px-4 py-2 bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 rounded-xl text-sm font-medium hover:bg-slate-200 dark:hover:bg-slate-700 cursor-pointer"
                  >
                    Mégse
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl text-sm font-medium transition cursor-pointer shadow-sm"
                  >
                    Mentés
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── DEMO ÁTMÁSOLÁSA ÉLES SZERVEZETKÉNT MODAL ── */}
      {cloneModalTenant && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-xs animate-in fade-in duration-200"
          onClick={() => !cloning && setCloneModalTenant(null)}
        >
          <div 
            className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl w-full max-w-lg p-6 shadow-2xl relative overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between pb-4 border-b border-slate-100 dark:border-slate-800">
              <div className="flex items-center gap-2.5">
                <div className="p-2 rounded-xl bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20">
                  <Rocket className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-slate-900 dark:text-white">
                    DEMO adatok átmásolása élesbe
                  </h3>
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    A tesztkörnyezetben elvégzett munka nem vész el
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => !cloning && setCloneModalTenant(null)}
                className="p-1 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 rounded-lg cursor-pointer"
              >
                ✕
              </button>
            </div>

            {cloneStatusMsg && (
              <div className={`mt-4 p-3 rounded-xl text-xs flex items-center gap-2 ${
                cloneStatusMsg.type === 'success' 
                  ? 'bg-emerald-50 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800' 
                  : 'bg-rose-50 text-rose-800 dark:bg-rose-950/40 dark:text-rose-300 border border-rose-200 dark:border-rose-800'
              }`}>
                {cloneStatusMsg.type === 'success' ? <Check className="w-4 h-4 shrink-0" /> : <Trash2 className="w-4 h-4 shrink-0" />}
                <span>{cloneStatusMsg.text}</span>
              </div>
            )}

            <form onSubmit={handleExecuteClone} className="mt-4 space-y-4">
              <div className="p-3 bg-slate-50 dark:bg-slate-950/50 rounded-xl border border-slate-200 dark:border-slate-800 text-xs space-y-1">
                <div className="text-slate-500 font-medium">Forrás DEMO szervezet:</div>
                <div className="font-bold text-slate-900 dark:text-white flex items-center gap-2">
                  <span>{cloneModalTenant.name}</span>
                  <span className="px-2 py-0.5 text-[10px] font-bold rounded-full bg-amber-500/10 text-amber-600 border border-amber-500/20">
                    DEMO
                  </span>
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1.5">
                  Új hivatalos éles szervezet neve *
                </label>
                <input
                  type="text"
                  required
                  value={cloneName}
                  onChange={(e) => setCloneName(e.target.value)}
                  className="w-full px-3.5 py-2 bg-slate-50 dark:bg-slate-950 border border-slate-300 dark:border-slate-700 rounded-xl text-slate-900 dark:text-slate-100 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 font-medium"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1.5">
                  Új szervezet URL azonosítója (slug) *
                </label>
                <input
                  type="text"
                  required
                  value={cloneSlug}
                  onChange={(e) => setCloneSlug(e.target.value)}
                  className="w-full px-3.5 py-2 bg-slate-50 dark:bg-slate-950 border border-slate-300 dark:border-slate-700 rounded-xl text-slate-900 dark:text-slate-100 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>

              <div className="p-3 bg-emerald-50/50 dark:bg-emerald-950/20 border border-emerald-200/60 dark:border-emerald-900/40 rounded-xl space-y-2">
                <label className="flex items-center gap-2 cursor-pointer select-none text-xs font-medium text-slate-800 dark:text-slate-200">
                  <input
                    type="checkbox"
                    checked={cloneCopyUsers}
                    onChange={(e) => setCloneCopyUsers(e.target.checked)}
                    className="w-4 h-4 rounded text-emerald-600 focus:ring-emerald-500"
                  />
                  <span>Felhasználók átmásolása és automatikus aktiválása</span>
                </label>
                <p className="text-[11px] text-slate-500 dark:text-slate-400 pl-6 leading-relaxed">
                  Minden közzétételi oldal, mezőstruktúra, zárszámadási rekord, melléklet és beállítás azonnal átkerül az új hivatalos éles szervezetbe. A DEMO szervezet pedig változatlanul megmarad további tesztekhez.
                </p>
              </div>

              <div className="pt-3 border-t border-slate-100 dark:border-slate-800 flex items-center justify-between">
                <button
                  type="button"
                  onClick={handlePromoteDirectly}
                  disabled={cloning}
                  className="text-xs font-medium text-amber-600 hover:text-amber-700 dark:text-amber-400 underline cursor-pointer disabled:opacity-50"
                  title="Nem hoz létre új másolatot, hanem a meglévő szervezetet kapcsolja élesre"
                >
                  Vagy közvetlen átállítás élesre
                </button>

                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setCloneModalTenant(null)}
                    disabled={cloning}
                    className="px-3.5 py-2 bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 rounded-xl text-xs font-medium hover:bg-slate-200 dark:hover:bg-slate-700 cursor-pointer"
                  >
                    Mégse
                  </button>
                  <button
                    type="submit"
                    disabled={cloning || !cloneName}
                    className="flex items-center gap-1.5 px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white rounded-xl text-xs font-bold transition cursor-pointer shadow-sm disabled:opacity-50"
                  >
                    {cloning ? (
                      <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <Rocket className="w-3.5 h-3.5" />
                    )}
                    <span>{cloning ? 'Másolás folyamatban...' : 'Másolás élesként'}</span>
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Szervezeti egyedi belépési link */}
      {loginLinkTenant && (
        <TenantLoginLinkModal
          tenantId={loginLinkTenant.id}
          tenantName={loginLinkTenant.name}
          onClose={() => setLoginLinkTenant(null)}
        />
      )}

      {/* Közzétételi Sablon Telepítő Modal */}
      <TemplateProvisionModal
        isOpen={provisionModalOpen}
        onClose={() => setProvisionModalOpen(false)}
        tenantId={targetTenantForProvision?.id}
        tenantName={targetTenantForProvision?.name}
        onSuccess={loadTenants}
      />

      {/* Sablon – batch mód (csak superadmin) */}
      {role === 'superadmin' && (
        <TemplateBatchModal isOpen={batchModalOpen} onClose={() => setBatchModalOpen(false)} onDone={loadTenants} />
      )}
      {role === 'superadmin' && (
        <QuotaAdminModal isOpen={quotaOpen} onClose={() => { setQuotaOpen(false); loadQuotaInfo(); }} onChanged={() => { loadTenants(); loadQuotaInfo(); }} />
      )}
      {role === 'superadmin' && (
        <TemplateEditorModal isOpen={templateEditorOpen} onClose={() => setTemplateEditorOpen(false)} />
      )}

      {/* Szuperadmin Környezeti Szervezet-Összehasonlító Modal */}
      <TenantDiffModal
        isOpen={diffModalOpen}
        onClose={() => setDiffModalOpen(false)}
        onTenantDeleted={loadTenants}
      />
    </div>
  );
};
